import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalContext } from '../../context/LocalContext';
import './Hero.css';

const Hero = () => {
  const { openSignupModal, openLoginModal, showNotification } = useLocalContext();
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeProfile, setActiveProfile] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState('');
  const [showSwipeIntroPopup, setShowSwipeIntroPopup] = useState(false);
  const [pendingSwipeDirection, setPendingSwipeDirection] = useState('');
  const [visitorName, setVisitorName] = useState('');
  const [visitorAge, setVisitorAge] = useState('');
  const [swipeFeedback, setSwipeFeedback] = useState('');
  const [showMessageCenter, setShowMessageCenter] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(3);
  const [incomingNotification, setIncomingNotification] = useState(null);

  // Hero slides data
  /* Same crop dimensions so every slide layer matches */
  const slideImg = (id) =>
    `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1200&h=1600&q=80`;

  const slides = [
    {
      id: 1,
      headline: "Swipe Right®",
      subtext: "Create a new connection today",
      image: slideImg('photo-1522673607200-164d1b6ce486'),
      gradient: "linear-gradient(135deg, #fd267a 0%, #ff6b6b 100%)"
    },
    {
      id: 2,
      headline: "Make Friends",
      subtext: "Meet new people in your area",
      image: slideImg('photo-1517230878791-4d28214057c2'),
      gradient: "linear-gradient(135deg, #fe3c72 0%, #ff8a5c 100%)"
    },
    {
      id: 3,
      headline: "Find Love",
      subtext: "55 billion matches made",
      image: slideImg('photo-1529686342540-1b43aec0df75'),
      gradient: "linear-gradient(135deg, #ff6b6b 0%, #fd267a 100%)"
    }
  ];

  const previewProfiles = [
    {
      id: 1,
      name: 'Kiara',
      age: 24,
      city: 'Downtown',
      vibe: 'Cocktail nights and beach drives',
      image: slideImg('photo-1488426862026-3ee34a7d66df')
    },
    {
      id: 2,
      name: 'Nisha',
      age: 26,
      city: 'City Center',
      vibe: 'Dance classes and rooftop cafes',
      image: slideImg('photo-1487412720507-e7ab37603c6f')
    },
    {
      id: 3,
      name: 'Aanya',
      age: 25,
      city: 'West Side',
      vibe: 'Weekend getaways and deep talks',
      image: slideImg('photo-1485290334039-a3c69043e517')
    }
  ];

  const messageCenterItems = [
    { id: 1, name: 'Kiara', text: 'Loved your vibe. Want to chat?', time: '10:12 AM' },
    { id: 2, name: 'Nisha', text: 'You seem fun. Coffee this week?', time: '10:19 AM' },
    { id: 3, name: 'Aanya', text: 'Hi! You have a great profile.', time: '10:26 AM' }
  ];
  const [liveMessages, setLiveMessages] = useState(messageCenterItems);
  const likeMessageTimers = useRef([]);

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  useEffect(() => {
    if (!swipeDirection) return undefined;

    const timeout = setTimeout(() => {
      setActiveProfile((prev) => (prev + 1) % previewProfiles.length);
      setSwipeDirection('');
    }, 260);

    return () => clearTimeout(timeout);
  }, [swipeDirection, previewProfiles.length]);

  useEffect(() => {
    const timers = likeMessageTimers.current;
    return () => {
      timers.forEach((timerId) => clearTimeout(timerId));
    };
  }, []);

  useEffect(() => {
    const savedVisitor = localStorage.getItem('findLoveVisitorProfile');
    if (!savedVisitor) return;

    try {
      const parsed = JSON.parse(savedVisitor);
      if (parsed?.name && parsed?.age) {
        setVisitorName(parsed.name);
        setVisitorAge(String(parsed.age));
      }
    } catch {
      // Ignore malformed local storage payload.
    }
  }, []);

  const handleSwipe = (direction) => {
    if (swipeDirection) return;
    if (!visitorName || !visitorAge) {
      setPendingSwipeDirection(direction);
      setShowSwipeIntroPopup(true);
      return;
    }
    const activeProfileData = previewProfiles[activeProfile];
    if (activeProfileData) {
      const actionWord = direction === 'right' ? 'liked' : 'passed';
      setSwipeFeedback(`You ${actionWord} ${activeProfileData.name}`);
      setUnreadMessages((prev) => Math.min(prev + 1, 9));

      if (direction === 'right') {
        const timerId = setTimeout(() => {
          const now = new Date();
          const formattedTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          const newMessage = {
            id: Date.now(),
            name: activeProfileData.name,
            text: 'Hey, I am interested. Let us talk.',
            time: formattedTime
          };

          setLiveMessages((prev) => [newMessage, ...prev].slice(0, 8));
          setUnreadMessages((prev) => Math.min(prev + 1, 9));
          setIncomingNotification({
            id: Date.now(),
            title: `${activeProfileData.name} sent a message`,
            text: newMessage.text,
            time: formattedTime
          });
        }, 10000);
        likeMessageTimers.current.push(timerId);
      }
    }
    setSwipeDirection(direction);
  };

  useEffect(() => {
    if (!swipeFeedback) return undefined;
    const timeout = setTimeout(() => setSwipeFeedback(''), 1500);
    return () => clearTimeout(timeout);
  }, [swipeFeedback]);

  const openMessageCenter = () => {
    setShowMessageCenter(true);
    setUnreadMessages(0);
    setIncomingNotification(null);
  };

  useEffect(() => {
    if (!incomingNotification) return undefined;
    const timeout = setTimeout(() => setIncomingNotification(null), 3500);
    return () => clearTimeout(timeout);
  }, [incomingNotification]);

  const handleSwipeIntroSubmit = (e) => {
    e.preventDefault();

    const ageNumber = Number(visitorAge);
    if (!visitorName.trim()) {
      showNotification('Please enter your name before swiping.', 'warning');
      return;
    }
    if (!Number.isFinite(ageNumber) || ageNumber < 18) {
      showNotification('Please enter a valid age (18+).', 'warning');
      return;
    }

    const payload = { name: visitorName.trim(), age: ageNumber };
    localStorage.setItem('findLoveVisitorProfile', JSON.stringify(payload));
    setVisitorName(payload.name);
    setVisitorAge(String(payload.age));
    setShowSwipeIntroPopup(false);

    if (pendingSwipeDirection) {
      setSwipeDirection(pendingSwipeDirection);
      setPendingSwipeDirection('');
    }
  };

  // App Store buttons
  const AppleStoreButton = () => (
    <img 
      src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
      alt="Download on the App Store" 
      className="store-button" 
    />
  );

  const GoogleStoreButton = () => (
    <img 
      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
      alt="Get it on Google Play" 
      className="store-button" 
    />
  );

  return (
    <section className="hero">
      {/* Background Slides */}
      <div className="hero-slides">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`hero-slide ${index === activeSlide ? 'active' : ''}`}
            style={{ background: slide.gradient }}
          >
            <div className="hero-slide-bg" style={{ backgroundImage: `url(${slide.image})` }} />
          </div>
        ))}
      </div>

      {/* Overlay */}
      <div className="hero-overlay" />

      <div className="hero-inner">
        {/* Content */}
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-headline">
              {slides[activeSlide].headline}
            </h1>
            <p className="hero-subtext">
              {slides[activeSlide].subtext}
            </p>
          </div>

          <div className="hero-feature-pills">
            <span>Verified Profiles</span>
            <span>Smart Matching</span>
            <span>Real-time Chat</span>
          </div>

          <div className="hero-cta">
            <div className="hero-cta-buttons">
              <button className="btn btn-primary btn-lg hero-btn" onClick={() => navigate('/find-love')}>
                Find Love Now
              </button>
              <button className="btn btn-lg hero-btn hero-btn-outline" onClick={openSignupModal}>
                Join Free
              </button>
            </div>
          </div>

          {/* App Store Buttons */}
          <div className="hero-app-buttons">
            <a href="#" className="app-store-link" aria-label="Download on App Store">
              <AppleStoreButton />
            </a>
            <a href="#" className="app-store-link" aria-label="Get it on Google Play">
              <GoogleStoreButton />
            </a>
          </div>

          <div className="hero-stats-strip">
            <div>
              <strong>55B+</strong>
              <span>Matches</span>
            </div>
            <div>
              <strong>190+</strong>
              <span>Countries</span>
            </div>
            <div>
              <strong>1.5M+</strong>
              <span>Dates / Week</span>
            </div>
          </div>

          <p className="hero-trust-note">
            Trusted by millions worldwide to discover meaningful connections.
          </p>

          {/* Slide Indicators */}
          <div className="hero-indicators">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === activeSlide ? 'active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Phone Mockup */}
        <div className="hero-phone">
          <div className="phone-frame">
            <div className="phone-screen">
              <div className="phone-card">
                {previewProfiles.map((profile, index) => {
                  const relativeIndex = (index - activeProfile + previewProfiles.length) % previewProfiles.length;
                  const isActiveCard = relativeIndex === 0;
                  const cardClass = [
                    'swipe-preview-card',
                    isActiveCard ? 'active' : 'stacked',
                    isActiveCard && swipeDirection ? `swipe-${swipeDirection}` : ''
                  ].join(' ').trim();

                  return (
                    <article key={profile.id} className={cardClass} aria-hidden={!isActiveCard}>
                      <img src={profile.image} alt={`${profile.name}, ${profile.age}`} />
                      <div className="phone-card-overlay" />
                      <div className="phone-card-info">
                        <span className="phone-card-name">{profile.name}, {profile.age}</span>
                        <span className="phone-card-location">{profile.city}</span>
                        <p className="phone-card-vibe">{profile.vibe}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
              <div className="swipe-actions" role="group" aria-label="Swipe preview controls">
                <button
                  type="button"
                  className="swipe-action-btn pass"
                  onClick={() => handleSwipe('left')}
                >
                  Pass
                </button>
                <button
                  type="button"
                  className="swipe-action-btn like"
                  onClick={() => handleSwipe('right')}
                >
                  Like
                </button>
              </div>
            </div>
          </div>
          {swipeFeedback && <p className="hero-swipe-feedback">{swipeFeedback}</p>}
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="scroll-arrow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="hero-message-fab"
        onClick={openMessageCenter}
        aria-label="Open message center"
      >
        <span>Messages</span>
        {unreadMessages > 0 && <strong>{unreadMessages}</strong>}
      </button>

      {showMessageCenter && (
        <aside className="hero-message-panel" aria-label="Message center">
          <div className="hero-message-panel-header">
            <h4>Message Center</h4>
            <button type="button" onClick={() => setShowMessageCenter(false)}>x</button>
          </div>
          <div className="hero-message-list">
            {liveMessages.map((item) => (
              <article key={item.id} className="hero-message-item">
                <div>
                  <strong>{item.name}</strong>
                  <p>{item.text}</p>
                </div>
                <span>{item.time}</span>
              </article>
            ))}
          </div>
          <button
            type="button"
            className="hero-login-message-btn"
            onClick={() => {
              setShowMessageCenter(false);
              openLoginModal();
            }}
          >
            Log in to message
          </button>
        </aside>
      )}

      {incomingNotification && (
        <div className="hero-floating-incoming-notification" role="status" aria-live="polite">
          <div className="incoming-top-row">
            <strong>{incomingNotification.title}</strong>
            <span>{incomingNotification.time}</span>
          </div>
          <p>{incomingNotification.text}</p>
        </div>
      )}

      {showSwipeIntroPopup && (
        <div className="hero-swipe-popup-backdrop" role="dialog" aria-modal="true" aria-label="Enter details before swiping">
          <form className="hero-swipe-popup-card" onSubmit={handleSwipeIntroSubmit}>
            <h3>Start Swiping</h3>
            <p>Enter your name and age once to unlock swipe preview.</p>
            <label htmlFor="hero-swipe-name">Name</label>
            <input
              id="hero-swipe-name"
              type="text"
              value={visitorName}
              onChange={(e) => setVisitorName(e.target.value)}
              placeholder="Enter your name"
              autoComplete="name"
            />
            <label htmlFor="hero-swipe-age">Age</label>
            <input
              id="hero-swipe-age"
              type="number"
              min="18"
              max="99"
              value={visitorAge}
              onChange={(e) => setVisitorAge(e.target.value)}
              placeholder="Enter your age"
            />
            <button type="submit" className="btn btn-primary btn-lg">Continue Swiping</button>
          </form>
        </div>
      )}
    </section>
  );
};

export default Hero;
